/* The server-rendered format remains readable when JavaScript is unavailable. */
(() => {
  if (customElements.get('atlas-event-championship')) return;
  let serial = 0;
  const svgNS = 'http://www.w3.org/2000/svg';

  class AtlasEventChampionship extends HTMLElement {
    connectedCallback() {
      if (this.controller || this.startupController) return;
      this.startupController = new AbortController();
      const startup = this.startupController;
      const initialize = () => {
        if (!this.isConnected || this.startupController !== startup) return;
        if (document.readyState === 'loading' || !this.querySelector('[data-aeb-panel]')) return;
        this.startupObserver.disconnect();
        startup.abort();
        this.startupController = null;
        this.initialize();
      };
      this.startupObserver = new MutationObserver(initialize);
      this.startupObserver.observe(this, {childList: true, subtree: true});
      document.addEventListener('DOMContentLoaded', initialize, {once: true, signal: startup.signal});
      initialize();
    }

    initialize() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.panels = [...this.querySelectorAll('[data-aeb-panel]')];
      this.tabs = [...this.querySelectorAll('[data-aeb-tab]')];
      this.mobileOpen ||= new Map();
      this.markerId ||= 'event-bracket-arrow-' + (++serial);
      this.querySelector('[data-aeb-tabs]').hidden = false;
      this.setAttribute('data-ready', '');
      this.addEventListener('click', event => {
        const tab = event.target.closest('[data-aeb-tab]');
        if (tab && this.contains(tab)) this.select(tab.dataset.aebTab);
      }, {signal});
      this.addEventListener('keydown', event => {
        const tab = event.target.closest('[data-aeb-tab]');
        if (!tab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        let index = this.tabs.indexOf(tab);
        if (event.key === 'Home') index = 0;
        else if (event.key === 'End') index = this.tabs.length - 1;
        else index = (index + (event.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
        this.select(this.tabs[index].dataset.aebTab, true);
      }, {signal});
      this.addEventListener('toggle', event => {
        if (event.target.matches('[data-aeb-heats]') && this.clientWidth <= 680) {
          this.mobileOpen.set(event.target.closest('[data-aeb-panel]').dataset.aebPanel, event.target.open);
        }
        this.schedule();
      }, {capture: true, signal});
      document.addEventListener('shopify:block:select', event => {
        const id = String(event.detail?.blockId || '');
        const panel = this.panels.find(item => item.dataset.aebPanel === id);
        if (panel) this.select(panel.dataset.aebPanel);
      }, {signal});
      this.resizeObserver = new ResizeObserver(() => {
        this.syncDisclosures();
        this.schedule();
      });
      this.resizeObserver.observe(this);
      this.select(this.activeId || this.panels[0]?.dataset.aebPanel);
      const controller = this.controller;
      document.fonts?.ready.then(() => {
        if (this.isConnected && this.controller === controller) this.schedule();
      });
    }

    disconnectedCallback() {
      this.startupController?.abort();
      this.startupController = null;
      this.startupObserver?.disconnect();
      this.controller?.abort();
      this.controller = null;
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      this.lastNarrow = undefined;
    }

    select(id, focus = false) {
      const panel = this.panels.find(item => item.dataset.aebPanel === id) || this.panels[0];
      if (!panel) return;
      this.activeId = panel.dataset.aebPanel;
      this.panels.forEach(item => {
        const selected = item === panel;
        item.hidden = !selected;
        item.setAttribute('role', 'tabpanel');
        item.setAttribute('aria-labelledby', this.tabs.find(tab => tab.dataset.aebTab === item.dataset.aebPanel)?.id || '');
      });
      this.tabs.forEach(tab => {
        const selected = tab.dataset.aebTab === this.activeId;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
        if (selected && focus) tab.focus();
      });
      this.syncDisclosures(true);
      this.schedule();
    }

    syncDisclosures(force = false) {
      const narrow = this.clientWidth <= 680;
      if (!force && narrow === this.lastNarrow) return;
      this.lastNarrow = narrow;
      for (const panel of this.panels) {
        panel.querySelector('[data-aeb-heats]').open = narrow ? this.mobileOpen.get(panel.dataset.aebPanel) === true : true;
      }
    }

    schedule() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => {
        if (this.isConnected && this.controller) this.draw();
      });
    }

    draw() {
      const panel = this.panels.find(item => !item.hidden);
      if (!panel) return;
      const chart = panel.querySelector('[data-aeb-chart]');
      const svg = panel.querySelector('[data-aeb-lines]');
      const direct = panel.querySelector('[data-aeb-direct]');
      const redemption = panel.querySelector('[data-aeb-redemption]');
      const final = panel.querySelector('[data-aeb-final]');
      const details = panel.querySelector('[data-aeb-heats]');
      const base = chart.getBoundingClientRect();
      if (!base.width || !base.height) return;
      const rect = element => {
        const r = element.getBoundingClientRect();
        return {x: r.left - base.left, y: r.top - base.top, w: r.width, h: r.height};
      };
      const centre = r => r.x + r.w / 2;
      const d = rect(direct), r = rect(redemption), f = rect(final);
      const anchors = details.open ? [...panel.querySelectorAll('[data-aeb-heat]')].map(rect) : [rect(details.querySelector('summary'))];
      const bottom = Math.max(...anchors.map(a => a.y + a.h));
      const junction = bottom + Math.max(14, (Math.min(d.y, r.y) - bottom) * .43);
      const xDirect = centre(d), xRedemption = centre(r), xFinal = centre(f);
      const paths = [];
      anchors.forEach(a => paths.push([`M${centre(a)} ${a.y + a.h} V${junction}`, 'normal']));
      paths.push([`M${Math.min(xDirect, ...anchors.map(centre))} ${junction} H${Math.max(xRedemption, ...anchors.map(centre))}`, 'normal']);
      paths.push([`M${xDirect} ${junction} V${d.y - 5}`, 'direct']);
      paths.push([`M${xRedemption} ${junction} V${r.y - 5}`, 'redemption']);
      const mergeY = Math.max(d.y + d.h, r.y + r.h) + (f.y - Math.max(d.y + d.h, r.y + r.h)) * .5;
      paths.push([`M${xDirect} ${d.y + d.h} V${mergeY} H${xFinal}`, 'direct']);
      paths.push([`M${xRedemption} ${r.y + r.h} V${mergeY} H${xFinal}`, 'redemption']);
      paths.push([`M${xFinal} ${mergeY} V${f.y - 5}`, 'final']);
      const node = (tag, attributes) => {
        const element = document.createElementNS(svgNS, tag);
        Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, String(value)));
        return element;
      };
      const markerId = this.markerId + '-' + this.panels.indexOf(panel);
      const defs = node('defs', {});
      const marker = node('marker', {id: markerId, viewBox: '0 0 8 8', refX: 7, refY: 4, markerWidth: 6, markerHeight: 6, orient: 'auto-start-reverse'});
      marker.append(node('path', {d: 'M1 1L7 4L1 7', fill: 'none', stroke: 'currentColor', 'stroke-width': 1.2}));
      defs.append(marker);
      svg.setAttribute('viewBox', `0 0 ${base.width} ${base.height}`);
      svg.setAttribute('width', String(base.width));
      svg.setAttribute('height', String(base.height));
      svg.replaceChildren(defs, ...paths.map(([d, kind]) => node('path', {d, class: 'aeb__connector aeb__connector--' + kind, ...(kind === 'normal' ? {} : {'marker-end': `url(#${markerId})`})})));
    }
  }

  customElements.define('atlas-event-championship', AtlasEventChampionship);
})();
